import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { post, del, patch } from '../lib/api';
import TopBar from '../components/layout/TopBar';
import SectionCard from '../components/widgets/SectionCard';
import { Plus, Trash2, Briefcase, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

export default function CentrosCusto() {
  const { companies, costCenters, reloadCostCenters } = useApp();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ company_id: '', name: '', code: '' });

  const openCreate = () => {
    setEditingId(null);
    setForm({ company_id: '', name: '', code: '' });
    setOpen(true);
  };

  const openEdit = (cc) => {
    setEditingId(cc.id);
    setForm({
      company_id: cc.company_id || '',
      name: cc.name || '',
      code: cc.code || '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nome obrigatório');
    const payload = { ...form, company_id: form.company_id || null, code: form.code || null };
    try {
      if (editingId) {
        await patch(`/cost-centers/${editingId}`, payload);
        toast.success('Centro de custo atualizado');
      } else {
        await post('/cost-centers', payload);
        toast.success('Centro de custo criado');
      }
      setOpen(false);
      setEditingId(null);
      setForm({ company_id: '', name: '', code: '' });
      reloadCostCenters();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir?')) return;
    await del(`/cost-centers/${id}`);
    reloadCostCenters();
  };

  return (
    <div className="fade-in">
      <TopBar
        title="Centros de Custo"
        subtitle={`${costCenters.length} centros cadastrados`}
        action={
          <Button onClick={openCreate} className="btn-gold rounded-md gap-1.5 px-4" data-testid="btn-new-cc">
            <Plus size={15} /> Novo Centro
          </Button>
        }
      />
      <div className="px-8 py-7">
        <SectionCard padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full ceap-table">
              <thead>
                <tr>
                  <th>Centro de Custo</th>
                  <th>Empresa</th>
                  <th>Código</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {costCenters.map((cc) => {
                  const co = companies.find((c) => c.id === cc.company_id);
                  return (
                    <tr key={cc.id} data-testid={`cc-row-${cc.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <Briefcase size={13} className="text-zinc-500" />
                          <span className="text-zinc-100">{cc.name}</span>
                        </div>
                      </td>
                      <td className="text-zinc-300 text-xs">{co?.name || '—'}</td>
                      <td className="font-mono text-zinc-400 text-xs">{cc.code || '—'}</td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEdit(cc)}
                            className="p-1.5 rounded hover:bg-amber-900/30 text-zinc-400 hover:text-amber-400"
                            data-testid={`btn-edit-cc-${cc.id}`}
                            title="Editar"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => remove(cc.id)}
                            className="p-1.5 rounded hover:bg-red-900/30 text-zinc-400 hover:text-red-400"
                            data-testid={`btn-delete-cc-${cc.id}`}
                            title="Excluir"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? 'Editar Centro de Custo' : 'Novo Centro de Custo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Empresa</Label>
              <Select value={form.company_id || 'none'} onValueChange={(v) => setForm({ ...form, company_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800 mt-1"><SelectValue placeholder="Empresa (opcional)" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  <SelectItem value="none">— Compartilhado —</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1" data-testid="input-cc-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-zinc-900 border-zinc-800 mt-1 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }} className="text-zinc-400 hover:bg-zinc-900">Cancelar</Button>
            <Button onClick={submit} className="btn-gold" data-testid="btn-save-cc">
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
